import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Map, Users, BarChart3, Settings, Shield, Layers, Gauge, Receipt, Globe } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const HERO_IMG = 'https://images.unsplash.com/photo-1767334850671-7bcb6e9192e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBmbGVldCUyMG5pZ2h0JTIwaGlnaHdheSUyMGFlcmlhbHxlbnwwfHx8fDE3NzMyNjc4MTV8MA&ixlib=rb-4.1.0&q=85&w=1600';

const painPoints = [
  { icon: Map, problem: 'No fleet visibility', solution: 'See every truck on a single live map. Filter by status, driver, or route.' },
  { icon: Receipt, problem: 'Manual settlements', solution: 'Automated driver pay calculation. Commission, fuel advances, insurance - all computed instantly.' },
  { icon: Users, problem: 'Scattered driver data', solution: 'One place for licenses, hours, earnings, and performance. Organized by driver.' },
  { icon: BarChart3, problem: 'No revenue insights', solution: 'Revenue per mile, cost analysis, and fleet utilization dashboards. Data-driven decisions.' },
];

const features = [
  { icon: Gauge, title: 'Control Tower Dashboard', desc: 'Your mission control. Live fleet map, load statuses, revenue metrics, and driver activity - all in one dark-mode command center.' },
  { icon: Map, title: 'Global Fleet Map', desc: 'See every truck in real-time. Filter by active, idle, or en-route. Click any truck for details.' },
  { icon: Users, title: 'Driver Management', desc: 'Onboard drivers, assign loads, track earnings, and manage settlements. All from one screen.' },
  { icon: BarChart3, title: 'Analytics & Reports', desc: 'Revenue per mile, fleet utilization, driver performance, and cost breakdowns. Export to PDF.' },
  { icon: Globe, title: 'Multi-Tenant Architecture', desc: 'Run multiple trucking companies from one platform. Complete data isolation between organizations.' },
  { icon: Shield, title: 'Load State Machine', desc: 'Every load follows a strict lifecycle. Draft to Invoiced - every transition is logged and auditable.' },
  { icon: Settings, title: 'Automated Settlements', desc: 'Calculate driver pay with configurable commission rates, fuel advances, and insurance deductions.' },
  { icon: Layers, title: 'Client Portal Included', desc: 'Give your shippers their own portal. They book loads and track deliveries without calling you.' },
];

const metrics = [
  { value: '8', label: 'Load status stages' },
  { value: '100%', label: 'GPS coverage' },
  { value: 'Multi', label: 'Tenant ready' },
];

export default function FleetsLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="fleets-landing">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 flex items-center justify-between px-6 md:px-12 py-5 z-20 bg-[#09090B]/70 backdrop-blur-md border-b border-zinc-800/30" data-testid="fleets-nav">
        <div className="flex items-center gap-3">
          <BrandLogo size="default" darkBg={true} />
          <span className="text-xs text-zinc-500 border-l border-zinc-700 pl-3 hidden sm:block">For Fleet Managers</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate('/login')} data-testid="fleet-landing-login">
            Log In
          </Button>
          <Button className="bg-[#E5A100] hover:bg-[#E5A100]/100 text-white font-semibold rounded-full px-6"
            onClick={() => navigate('/register')} data-testid="fleet-landing-register">
            Get Started <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-24 md:pt-32 pb-32">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Fleet at night" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-[#E5A100]/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Gauge className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-medium tracking-wide">FLEET MANAGEMENT</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Command your fleet.<br />
            <span className="bg-gradient-to-r from-[#E5A100] via-[#E5A100] to-[#E5A100] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Control your margin.
            </span>
          </h1>
          <p className="text-zinc-400 text-lg md:text-xl max-w-xl mb-10 leading-relaxed">
            The all-in-one platform for trucking companies. Track every truck,
            manage every driver, automate every settlement - from a single command center.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button className="bg-[#E5A100] hover:bg-[#E5A100]/100 text-white font-semibold rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/register')} data-testid="fleet-hero-cta">
              Start Free Trial <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}>
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="border-y border-zinc-800 px-6 md:px-12 py-8">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8">
          {metrics.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-white">{s.value}</p>
              <p className="text-zinc-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pain Points */}
      <section className="px-6 md:px-12 py-20" data-testid="fleet-pain-points">
        <h2 className="text-base md:text-lg font-bold text-center mb-12">
          Running a fleet shouldn't mean <span className="text-red-400">running in circles</span>
        </h2>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {painPoints.map((p, i) => (
            <div key={i} className="border border-zinc-800 rounded-2xl p-6 hover:border-white/30 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-semibold text-red-400">{p.problem}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E5A100]/10 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-zinc-300">{p.solution}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Grid */}
      <section className="px-6 md:px-12 pb-20" data-testid="fleet-features">
        <h2 className="text-base md:text-lg font-bold text-center mb-12">
          Your <span className="text-white">mission control</span> for freight operations
        </h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {features.map((f, i) => (
            <div key={i} className="border border-zinc-800 bg-zinc-900/40 rounded-2xl p-6 hover:border-white/30 transition-all group">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#E5A100]/10 flex items-center justify-center group-hover:bg-[#E5A100]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold text-white">{f.title}</h3>
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed pl-14">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-3xl mx-auto text-center border border-zinc-700 rounded-3xl p-10 md:p-16 bg-gradient-to-br from-zinc-900 via-zinc-800/50 to-zinc-900">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Take control of your fleet</h2>
          <p className="text-zinc-400 mb-8 max-w-md mx-auto">
            Start with a free trial. No credit card required. Set up your fleet in under 10 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#E5A100] hover:bg-[#E5A100]/100 text-white font-semibold rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/register')} data-testid="fleet-bottom-cta">
              Start Free Trial <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}>
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 px-6 md:px-12 py-6 flex items-center justify-between">
        <BrandLogo size="sm" darkBg={true} className="opacity-50" />
        <div className="flex items-center gap-6 text-xs text-zinc-600">
          <button onClick={() => navigate('/')} className="hover:text-zinc-400 transition-colors">Home</button>
          <button onClick={() => navigate('/for-drivers')} className="hover:text-zinc-400 transition-colors">For Drivers</button>
          <button onClick={() => navigate('/for-shippers')} className="hover:text-zinc-400 transition-colors">For Shippers</button>
        </div>
      </footer>
    </div>
  );
}
